import React, { useRef } from "react";

import { ProgressStepModal, useProgressStepModal } from "src/components/Modal";
import { RulesForm, RulesFormRef } from "src/components/scenario/RulesForm";
import { useToast } from "src/components/Toasts/ToastContainer.tsx";
import { mapToModalError } from "src/errors";
import { useCreateScenarioMutation } from "src/graphql/anti-fraud/queries/scenarios/CreateScenarioMutation.ts";
import { useT } from "src/internationalization";
import { ShortScenario } from "src/types/scenario.ts";
import { cn } from "src/utils/string.utils";

import { RuleWithScore } from "./CreateScenario.types";
import { CreateScenarioForm, CreateScenarioFormRef } from "./CreateScenarioForm";

type CreateScenarioModalProps = {
  onClose: () => void;
  scenarios: ShortScenario[];
  caseTypeCode: string;
};

export const CreateScenarioModal: React.FC<CreateScenarioModalProps> = ({
  onClose,
  caseTypeCode,
  scenarios,
}) => {
  const t = useT();
  const { showSuccessToast } = useToast();

  const { performMutation, error, loading } = useCreateScenarioMutation({
    onSuccess: () => {
      showSuccessToast(t("page.scenarios.tab.requests.modal.create.toast.success.message"));
      onClose();
    },
  });

  const submitVersionForm = () => {
    createScenarioVersionFormRef.current?.submit(
      (data) => {
        dataRef.current = { ...dataRef.current, ...data };
        control.setStepError("scenario", false);
        control.goNextStep();
      },
      () => {
        control.setStepError("scenario", true);
      }
    )();
  };

  const submitRulesForm = () => {
    scenarioVersionRulesFormRef.current?.submit(
      async (data) => {
        dataRef.current.rules = data;
        control.setStepError("rules", false);

        await performMutation({
          code: dataRef.current.code,
          description: dataRef.current.description,
          name: dataRef.current.name,
          rules: dataRef.current.rules.map((rule) => ({
            ruleId: rule.id,
            score: rule.score,
          })),
          threshold: dataRef.current.threshold,
          caseTypeCode: caseTypeCode,
        });
      },
      () => {
        control.setStepError("rules", true);
      }
    )();
  };

  const { modalProps, control } = useProgressStepModal([
    {
      type: "scenario",
      title: t("create.scenario.modal.identification.tab.title"),
      subtitle: t("create.scenario.modal.identification.tab.subtitle"),
      submit: submitVersionForm,
      focus: "#name",
    },
    {
      type: "rules",
      title: t("rules.title"),
      subtitle: t("create.scenario.modal.rules.tab.subtitle"),
      submit: submitRulesForm,
      focus: "#searchBlacklistable",
    },
  ]);

  const createScenarioVersionFormRef = useRef<CreateScenarioFormRef | null>(null);
  const scenarioVersionRulesFormRef = useRef<RulesFormRef | null>(null);

  const dataRef = useRef<{
    rules: RuleWithScore[];
    name: string;
    code: string;
    threshold: number;
    description: string;
  }>({
    rules: [],
    name: "",
    code: "",
    threshold: 0,
    description: "",
  });

  return (
    <ProgressStepModal
      kind="horizontal"
      onClose={onClose}
      title={t("create.scenario.modal.title")}
      subtitle={t("scenarios.title")}
      loading={loading}
      error={mapToModalError(error)}
      noPaddingX
      initialFocus="#scenarioName"
      confirmButtonText={t("general.actions.create")}
      {...modalProps}
    >
      <div
        className={cn("flex flex-col px-5 h-full", {
          hidden: !control.isStep("scenario"),
        })}
      >
        <CreateScenarioForm
          scenarios={scenarios}
          ref={createScenarioVersionFormRef}
          onValidChange={(isValid) => control.setStepError("scenario", !isValid)}
        />
      </div>
      <div
        className={cn("flex flex-col h-full overflow-hidden", {
          hidden: !control.isStep("rules"),
        })}
      >
        <RulesForm
          caseTypeCode={caseTypeCode}
          title={t("rules.title")}
          subtitle={t("create.scenario.modal.rules.tab.description")}
          ref={scenarioVersionRulesFormRef}
          onValidChange={(isValid) => control.setStepError("rules", !isValid)}
        />
      </div>
    </ProgressStepModal>
  );
};
